import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { usePermissions } from "contexts/PermissionsContext"

import { useTimezone } from "hooks/useTimezone"

import { PERMISSIONS } from "constants/permissions"

import { kycService } from "services/kyc/kyc.service"
import { Coin, GetSingleUser } from "services/user/type"

import { Maybe } from "types/common"

import { ReactComponent as BlockIcon } from "../../../assets/icons/block.svg"
import { ReactComponent as BubbleIcon } from "../../../assets/icons/bubble.svg"
import { ReactComponent as FileCheckIcon } from "../../../assets/icons/file-check.svg"
import person from "../../../assets/images/person.png"
import { AddUserBonus } from "../actions/AddUserBonus"
import { UserAccountPolicySection } from "../sections/UserAccountPolicySection"

interface UserProfileHeaderProps {
  userResult: Maybe<GetSingleUser>
  handleToggleExclusion: () => void
  updateKey: number
  handleAddBonusSuccess: () => void
  setOpenAddBonus: (value: boolean) => void
  setOpenSendMessage: (value: boolean) => void
  onUpdate?: () => void
}

export const UserProfileHeader = ({
  userResult,
  handleToggleExclusion,
  updateKey,
  handleAddBonusSuccess,
  setOpenAddBonus,
  setOpenSendMessage,
  onUpdate
}: UserProfileHeaderProps) => {
  if (!userResult) return null

  const { has } = usePermissions()
  // UserAccountPolicy gates GGR exclusion and streamer flags — admin-only data.
  const canSeeGgrInsight = has(PERMISSIONS.GGR_INSIGHT_VIEW)
  const { formatDate } = useTimezone()
  const [kycRequestSent, setKycRequestSent] = useState(false)
  const [kycRequestLoading, setKycRequestLoading] = useState(false)

  useEffect(() => {
    if (!userResult?.id) return
    kycService
      .hasRecentKYCRequest(userResult.id)
      .then((hasRecentRequest) => setKycRequestSent(hasRecentRequest))
      .catch(() => {
        toast.error("Failed to check KYC request status (is recently sent in 24 hours)")
        setKycRequestSent(false)
      })
  }, [userResult.id])

  const handleRequestKYC = async () => {
    setKycRequestLoading(true)
    try {
      await kycService.sendKYCRequest(userResult.id)
      setKycRequestSent(true)
      toast.success("KYC Request Email Sent")
    } catch (error: any) {
      toast.error(error?.data?.message ?? error?.message ?? "Failed to send KYC request")
    } finally {
      setKycRequestLoading(false)
    }
  }

  const isExcludedOrNotActivated =
    !userResult?.activated ||
    (userResult.exclusion_start &&
      userResult.exclusion_end &&
      new Date() >= new Date(userResult.exclusion_start) &&
      new Date() <= new Date(userResult.exclusion_end))

  const exclusionButtonText = (() => {
    if (!userResult) return "Exclude"
    if (!userResult.activated) return "Permanently excluded"
    if (userResult.exclusion_end) {
      const endDate = new Date(userResult.exclusion_end)
      if (new Date() <= endDate) {
        return `Excluded until ${formatDate(endDate)}`
      }
    }
    return "Exclude"
  })()

  return (
    <div className="flex flex-col gap-8 sm:flex-row sm:gap-4 justify-between p-6 w-full rounded-lg border border-[rgba(0,0,0,0.1)] dark:border-transparent bg-[rgba(212,212,212,0.1)] dark:bg-[rgba(255,255,255,0.04)] text-black dark:text-white backdrop-blur-md">
      <div className="flex gap-6 items-center">
        <div className="rounded-full overflow-hidden border border-[rgba(0,0,0,0.2)] dark:border-transparent">
          <img src={person} alt="Person image" className="w-24 min-h-24 shrink-0" />
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <div className="flex gap-2 flex-nowrap">
              {userResult.personal_profile.first_name} {userResult.personal_profile.last_name}
            </div>
          </div>
          <div className="flex gap-4 text-base items-start">
            <div>
              {!isExcludedOrNotActivated ? (
                <div className="rounded-[4px] py-[6px] px-3 bg-[rgba(16,185,129,0.2)] text-[rgba(16,185,129,1)]">Active</div>
              ) : (
                <div className="rounded-[4px] py-[6px] px-3 bg-[rgba(185,16,67,0.2)] text-[#b9105f]">Not Active</div>
              )}
            </div>
            {canSeeGgrInsight && (
              <UserAccountPolicySection
                userId={userResult.id}
                customHalls={userResult.personal_profile?.custom_halls || []}
                policy={userResult.account_policy}
                refreshKey={updateKey}
                onUpdate={onUpdate}
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap justify-start w-fit sm:flex-row gap-3 items-center">
        <button
          onClick={() => setOpenSendMessage(true)}
          disabled={!userResult.email_address}
          className={clsx(
            "flex gap-3 py-2 px-6 rounded-[4px] text-white bg-[rgba(16,185,129,1)] items-center transition-all hover:bg-[#0a7c56]",
            !userResult.email_address && "opacity-60 cursor-not-allowed hover:bg-[rgba(16,185,129,1)]"
          )}
          title={!userResult.email_address ? "User has no email address" : "Send message"}
          type="button"
        >
          <BubbleIcon /> Message
        </button>
        <button
          onClick={handleRequestKYC}
          disabled={kycRequestSent || kycRequestLoading}
          className={clsx(
            "flex gap-3 py-2 px-6 rounded-[4px] items-center transition-all text-white",
            kycRequestSent || kycRequestLoading
              ? "bg-gray-400 cursor-not-allowed opacity-60"
              : "bg-[rgba(139,92,246,1)] hover:bg-[rgba(109,62,216,1)]"
          )}
        >
          <FileCheckIcon /> {kycRequestSent ? "KYC Requested" : kycRequestLoading ? "Sending..." : "Request KYC"}
        </button>
        <AddUserBonus
          userId={userResult.id}
          bonusBalance={userResult.coins[Coin.B_USD] ?? undefined}
          onSuccess={handleAddBonusSuccess}
          key={`add-user-bonus-${updateKey}`}
          setOpen={setOpenAddBonus}
        />
        <button
          onClick={handleToggleExclusion}
          className={clsx(
            "flex gap-3 py-2 px-6 rounded-[4px] items-center transition-all",
            userResult?.activated
              ? "text-white bg-[rgba(239,68,68,1)] hover:bg-[#d43c3c]"
              : "text-white bg-gray-500 hover:bg-gray-600 cursor-pointer"
          )}
        >
          <BlockIcon /> {exclusionButtonText}
        </button>
      </div>
    </div>
  )
}
